<script setup lang="ts">
import ContentView from './ContentView.vue'
import SpinnerOverlay from '@/components/common/SpinnerOverlay.vue'
import SalesRepContactButton from '@/components/common/SalesRepContactButton.vue'
import DivisionsWidget from '@/components/widgets/divisions-widget/DivisionsWidget.vue'
import PortfolioWidget from '@/components/widgets/portfolio-widget/PortfolioWidget.vue'
// import RadialBarChart from '@/components/widgets/common/RadialBarChart.vue'
import RadialBarChartTwo from '@/components/widgets/common/RadialBarChartTwo.vue'
import ProductsTable from '@/components/widgets/portfolio-widget/ProductsTable.vue'
import VidacorpLogo from '@/components/icons/VidacorpLogo.vue'
import ApohealthLogo from '@/components/icons/ApohealthLogo.vue'
import ChemistsOwnLogo from '@/components/icons/ChemistsOwnLogo.vue'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useThemeStore, useValueDataStore } from '@/stores'
import { transformDataToRadialChartFormat } from '@/components/common/common'
import ExportButton from '@/components/common/ExportButton.vue'
import ExternalLinks from '@/components/common/ExternalLinks.vue'
import ErrorView from './ErrorView.vue'
import type { OpportunityProducts } from '@/data'
// import DataRefreshDatesButton from '@/components/common/DataRefreshDatesButton.vue'

const themeStore = useThemeStore()
const { themeColors } = themeStore
const { isValueDataStoreLoading, valueData, isError } = storeToRefs(useValueDataStore())

const colorProps = computed(() => ({
  primary: themeColors.colorVidacorp,
  secondary: themeColors.colorVidacorpDark,
  tertiary: themeColors.colorText
}))

const vidacorpTransformedData = ref(
  transformDataToRadialChartFormat(valueData.value.vidacorp, 'vidacorp')
)

const headerLogos = computed(() => [
  {
    portfolioName: 'Apohealth',
    logo: ApohealthLogo,
    logoProps: { height: '3.75rem', width: '9.375rem', fill: themeColors.colorText }
  },
  {
    portfolioName: `Chemists' Own`,
    logo: ChemistsOwnLogo,
    logoProps: { height: '3.75rem', width: '9.375rem', fill: themeColors.colorText }
  }
])

const selectedPortfolio = ref(valueData.value.vidacorp.portfolioValues[0]!.portfolioName)

const selectedOpportunityProducts = computed<OpportunityProducts>(() => {
  if (!selectedPortfolio.value) return [];

  return (
    valueData.value.vidacorp.portfolioValues
      .find((portfolio: any) => portfolio.portfolioName === selectedPortfolio.value)
      ?.opportunityProducts
      ?.map((p) => {
        if (["Apohealth", `Chemists' Own`, "Health & Wellness"].includes(selectedPortfolio.value)) {
          return {
            sku: p.sku,
            opportunityValue: p.opportunityValue,
          };
        }
        return null; // Explicitly return null for unmatched cases
      })
      ?.filter((p): p is NonNullable<typeof p> => p !== null) ?? [] // Remove null values
  );
});

function selectPortfolio(portfolioName: string) {
  selectedPortfolio.value =
    valueData.value.vidacorp.portfolioValues.find(
      (portfolio) => portfolio.portfolioName === portfolioName
    )?.portfolioName || ''
}

watch(valueData, () => {
  vidacorpTransformedData.value = transformDataToRadialChartFormat(
    valueData.value.vidacorp,
    'vidacorp'
  )
  selectedPortfolio.value = valueData.value.vidacorp.portfolioValues[0]!.portfolioName
})

</script>

<template>
  <SpinnerOverlay v-if="isValueDataStoreLoading" />
  <ErrorView v-else-if="isError"/>
  <ContentView v-else>
    <template #top>
      <!-- <DataRefreshDatesButton/> -->
      <ExternalLinks />
      <!-- <VidacorpLogo width="175px" height="50px" :fill="themeColors.colorText" /> -->
      <SalesRepContactButton label="Contact your VidaCorp Rep" type="vidacorp" />
      <ExportButton />
    </template>
    <template #middle>
      <div class="vidacorp-value-middle-left">
        <DivisionsWidget
          :isMain="false"
          :totalValue="valueData.vidacorp.value"
          :currentQuarterValue="valueData.vidacorp.valueCurrentQuarter"
          :opportunityValue="valueData.vidacorp.opportunity"
        >
          <template #value-title>
            <VidacorpLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorText" />
          </template>
          <template #chart>
            <RadialBarChartTwo :data="vidacorpTransformedData" :showLegend="true" />
            <!-- <RadialBarChart :data="vidacorpTransformedData" /> -->
          </template>
          <template #opportunity-title><span class="title">VidaCorp Opportunity</span></template>
        </DivisionsWidget>

        <PortfolioWidget
          :data="valueData.vidacorp.portfolioValues"
          :colors="colorProps"
          :headerLogos="headerLogos"
          :selectPortfolio="selectPortfolio"
        >
        </PortfolioWidget>
      </div>
      <div class="vidacorp-value-middle-right">
        <ProductsTable
          :data="selectedOpportunityProducts!"
          :portfolioName="selectedPortfolio"
          :color="themeColors.colorVidacorp"
          :glow="true"
        >
        </ProductsTable>
      </div>
    </template>
  </ContentView>
</template>

<style scoped>
.vidacorp-value-middle-left,
.vidacorp-value-middle-right {
  display: flex;
  flex: 0.5;
  flex-direction: row;
  justify-content: space-around;
  gap: 2.5%;
}

.title {
  font-size: var(--font-size-h2);
  font-weight: 600;
}
</style>
